import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import {
  Shield,
  AlertTriangle,
  Search,
  MoreVertical,
  Eye,
  Edit,
  CheckCircle,
  Archive,
  Clock,
  User,
  FileText,
  Download,
  Upload,
  Paperclip,
  Trash2,
  X
} from 'lucide-react';

import { projectService } from '../../../src/services/project.service';
import { fileStorageService } from '../../../src/services/file-storage.service';
import { showSuccess, showError } from '../../../src/utils/notification.utils';
import { notificationService } from '../../../src/services/notification.service';

// New Safety Incident Modal Component
function NewSafetyIncidentModal({ onIncidentCreated }: { onIncidentCreated?: () => void }) {
  const [open, setOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [projects, setProjects] = useState<any[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false); 
  const [formData, setFormData] = useState({
    // Form fields (all retained)
    title: '',
    project: '',
    incidentType: '',
    severity: '',
    description: '',
    reportedBy: 'Current User',
    incidentDate: '',
    incidentTime: '',
    location: '',
    workArea: '',
    witnessCount: '',
    witnesses: '',
    injuredPerson: '',
    injuredPersonCompany: '', // NEW - from API
    injuryType: '',
    medicalAttention: false,
    hospitalName: '', // NEW - from API
    hospitalTransport: false,
    workStopped: false,
    immediateActions: '',
    reportedToAuthorities: false, // NEW - from API
    authorityReportNumber: '', // NEW - from API
    assignedTo: '',
    status: 'reported',
    photos: [] as Array<{ file_id: string; file_path: string }>,
    notes: ''
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ id: string; name: string; size: number; type: string }>>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Fetch projects when modal opens
    useEffect(() => {
      if (open) {
        loadProjects();
      }
    }, [open]);
  
    const loadProjects = async () => {
      setLoadingProjects(true);
      try {
        const response = await projectService.getProjects();
        console.log('Safety Modal - Projects API Response:', response);
        if (response.success && response.data) {
          const projectData = Array.isArray(response.data)
            ? response.data
            : ((response.data as any).data || []);
          console.log('Safety Modal - Setting projects:', projectData);
          setProjects(projectData);
        }
      } catch (error) {
        console.error('Error loading projects:', error);
        showError({ title: 'Error', message: 'Failed to load projects' });
      } finally {
        setLoadingProjects(false);
      }
    };
  
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);

    try {
      const { documentService } = await import('../../../src/services/document.service');
      const { authService } = await import('../../../src/services/auth.service');
      const userProfile = await authService.getUserProfile();
      const orgId = userProfile?.organizationId;

      if (!orgId) {
        throw new Error('Organization ID not found');
      }

      const uploadPromises = Array.from(files).map(async (file) => {
        // Upload file to MinIO
        const uploadResponse = await documentService.uploadFile(file, orgId);

        if (!uploadResponse.success || !uploadResponse.data) {
          throw new Error(`Failed to upload ${file.name}`);
        }

        return {
          id: uploadResponse.data.storage_id,
          name: file.name,
          size: file.size,
          type: file.type,
          filePath: uploadResponse.data.storage_id
        };
      });

      const newFiles = await Promise.all(uploadPromises);
      setUploadedFiles([...uploadedFiles, ...newFiles]);

      // Update photos in form data with file_id and file_path objects
      const photoObjects = [...formData.photos, ...newFiles.map(f => ({ file_id: f.id, file_path: f.filePath }))];
      setFormData({ ...formData, photos: photoObjects });

      showSuccess({ title: 'Files Uploaded', message: `${newFiles.length} file(s) uploaded successfully` });

    } catch (error: any) {
      console.error('Error uploading files:', error);
      showError({ title: 'Upload Failed', message: error.message || 'Failed to upload files. Please try again.' });
    } finally {
      setIsUploading(false);
      // Reset input
      event.target.value = '';
    }
  };

  const handleRemoveFile = (fileId: string) => {
    setUploadedFiles(uploadedFiles.filter(f => f.id !== fileId));
    setFormData({
      ...formData,
      photos: formData.photos.filter(photo => photo.file_id !== fileId)
    });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Incident title is required';
    }
    if (!formData.project) {
      newErrors.project = 'Project selection is required';
    }
    if (!formData.incidentType) {
      newErrors.incidentType = 'Incident type is required';
    }
    if (!formData.severity) {
      newErrors.severity = 'Severity level is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Incident description is required';
    }
    if (!formData.incidentDate) {
      newErrors.incidentDate = 'Incident date is required';
    }
    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      const { safetyService } = await import('../../../src/services/safety.service');
      const { authService } = await import('../../../src/services/auth.service');

      // Get organization ID and user ID from user profile
      const userProfile = await authService.getUserProfile();
      const orgId = userProfile?.organizationId;
      const userId = userProfile?.id;

      if (!orgId) {
        showError({ title: 'Error', message: 'Organization ID not found' });
        setIsSaving(false);
        return;
      }

      if (!userId) {
        showError({ title: 'Error', message: 'User ID not found' });
        setIsSaving(false);
        return;
      }

      // Get next incident number
      const incidentNumberResponse = await safetyService.getNextIncidentNumber(formData.project);
      if (!incidentNumberResponse.success || !incidentNumberResponse.data) {
        showError({ title: 'Error', message: 'Failed to generate incident number' });
        setIsSaving(false);
        return;
      }

      // Combine date and time for incident_date
      const incidentDateTime = formData.incidentTime
        ? `${formData.incidentDate}T${formData.incidentTime}:00`
        : `${formData.incidentDate}T12:00:00`;

      // Convert witnesses textarea to array
      const witnessNamesArray = formData.witnesses
        ? formData.witnesses.split('\n').map(w => w.trim()).filter(w => w.length > 0)
        : [];

      // Prepare payload with ALL form fields
      const payload = {
        // Required fields
        organization_id: orgId,
        project_id: formData.project,
        incident_number: incidentNumberResponse.data.incident_number,
        incident_date: incidentDateTime,
        location: formData.location,
        description: formData.description,
        severity: formData.severity as 'minor' | 'moderate' | 'major' | 'critical',
        medical_attention_required: formData.medicalAttention,

        // Form-specific fields
        title: formData.title || undefined,
        incident_type: formData.incidentType || undefined,
        work_area: formData.workArea || undefined,
        witness_count: formData.witnessCount ? parseInt(formData.witnessCount) : undefined,

        // Injury details
        injured_person_name: formData.injuredPerson || undefined,
        injured_person_company: formData.injuredPersonCompany || undefined,
        injury_type: formData.injuryType || undefined,
        witness_names: witnessNamesArray.length > 0 ? witnessNamesArray : undefined,

        // Medical response
        immediate_action_taken: formData.immediateActions || undefined,
        hospital_name: formData.hospitalName || undefined,
        hospital_transport: formData.hospitalTransport || undefined,
        work_stopped: formData.workStopped || undefined,

        // Authority reporting
        reported_by: userId,
        reported_to_authorities: formData.reportedToAuthorities,
        authority_report_number: formData.authorityReportNumber || undefined,

        // Assignment & status
        assigned_to: formData.assignedTo || undefined,
        status: formData.status || undefined,

        // Additional
        photos: formData.photos.length > 0 ? formData.photos : undefined,
        notes: formData.notes || undefined
      };

      const response = await safetyService.createSafetyIncident(payload);

      if (response.success) {
        if (formData.assignedTo) {
          notificationService.incidentCreated(
            formData.assignedTo,
            formData.title || incidentNumberResponse.data.incident_number,
            formData.project,
          );
        }
        showSuccess({
          title: 'Safety Incident Reported',
          message: `Incident ${incidentNumberResponse.data.incident_number} has been reported successfully`
        });
        setOpen(false);

        // Call callback to reload incidents
        if (onIncidentCreated) {
          onIncidentCreated();
        }

        // Reset form
        setFormData({
          title: '',
          project: '',
          incidentType: '',
          severity: '',
          description: '',
          reportedBy: 'Current User',
          incidentDate: '',
          incidentTime: '',
          location: '',
          workArea: '',
          witnessCount: '',
          witnesses: '',
          injuredPerson: '',
          injuredPersonCompany: '',
          injuryType: '',
          medicalAttention: false,
          hospitalName: '',
          hospitalTransport: false,
          workStopped: false,
          immediateActions: '',
          reportedToAuthorities: false,
          authorityReportNumber: '',
          assignedTo: '',
          status: 'reported',
          photos: [],
          notes: ''
        });
        setUploadedFiles([]);
      } else {
        showError({ title: 'Error', message: 'Failed to report safety incident' });
      }
    } catch (error: any) {
      console.error('Error creating safety incident:', error);
      showError({
        title: 'Error',
        message: error?.message || 'Failed to report safety incident'
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <AlertTriangle className="h-4 w-4 mr-2" />
          Report Incident
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Report Safety Incident
          </DialogTitle>
          <DialogDescription>
            Report and document safety incidents or near misses
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Incident Information</h4>
            </div>

            <div className="space-y-2">
              <Label htmlFor="incident-title">Incident Title *</Label>
              <Input
                id="incident-title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="e.g., Fall from scaffolding - Level 3"
              />
              {errors.title && <p className="text-sm text-destructive">{errors.title}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="incident-project">Project *</Label>
                <Select value={formData.project} onValueChange={(value) => setFormData({...formData, project: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent> 
                  {projects.map((project) => (
                     <SelectItem key={project.project_id} value={project.project_id}>
                        {project.project_name || project.name || 'Unnamed Project'}
                      </SelectItem>
                    ))}                                   
                  </SelectContent>                  
                </Select>
                {errors.project && <p className="text-sm text-destructive">{errors.project}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="incident-type">Incident Type *</Label>
                <Select value={formData.incidentType} onValueChange={(value) => setFormData({...formData, incidentType: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="injury">Personal Injury</SelectItem>
                    <SelectItem value="near-miss">Near Miss</SelectItem>
                    <SelectItem value="property-damage">Property Damage</SelectItem>
                    <SelectItem value="environmental">Environmental Incident</SelectItem>
                    <SelectItem value="fire">Fire/Explosion</SelectItem>
                    <SelectItem value="equipment-failure">Equipment Failure</SelectItem>
                    <SelectItem value="unsafe-condition">Unsafe Condition</SelectItem>
                    <SelectItem value="unsafe-act">Unsafe Act</SelectItem>
                    <SelectItem value="first-aid">First Aid Case</SelectItem>
                    <SelectItem value="security">Security Incident</SelectItem>
                  </SelectContent>
                </Select>
                {errors.incidentType && <p className="text-sm text-destructive">{errors.incidentType}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="severity">Severity Level *</Label>
                <Select value={formData.severity} onValueChange={(value) => setFormData({...formData, severity: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select severity" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minor">Minor</SelectItem>
                    <SelectItem value="moderate">Moderate</SelectItem>
                    <SelectItem value="major">Major</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
                {errors.severity && <p className="text-sm text-destructive">{errors.severity}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="assigned-to">Assigned Investigator</Label>
                <Select value={formData.assignedTo} onValueChange={(value) => setFormData({...formData, assignedTo: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select investigator" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="safety-manager">Safety Manager</SelectItem>
                    <SelectItem value="project-manager">Project Manager</SelectItem>
                    <SelectItem value="site-supervisor">Site Supervisor</SelectItem>
                    <SelectItem value="safety-officer">Safety Officer</SelectItem>
                    <SelectItem value="external-investigator">External Investigator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="incident-description">Incident Description *</Label>
              <Textarea
                id="incident-description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Provide detailed description of what happened, how it occurred, and circumstances..."
                rows={4}
              />
              {errors.description && <p className="text-sm text-destructive">{errors.description}</p>}
            </div>
          </div>

          {/* Time & Location */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Time & Location</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="incident-date">Incident Date *</Label>
                <Input
                  id="incident-date"
                  type="date"
                  value={formData.incidentDate}
                  onChange={(e) => setFormData({...formData, incidentDate: e.target.value})}
                />
                {errors.incidentDate && <p className="text-sm text-destructive">{errors.incidentDate}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="incident-time">Incident Time</Label>
                <Input
                  id="incident-time"
                  type="time"
                  value={formData.incidentTime}
                  onChange={(e) => setFormData({...formData, incidentTime: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="incident-location">Location *</Label>
                <Input
                  id="incident-location"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  placeholder="e.g., Building A - Level 3, North Wing"
                />
                {errors.location && <p className="text-sm text-destructive">{errors.location}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="work-area">Work Area/Zone</Label>
                <Input
                  id="work-area"
                  value={formData.workArea}
                  onChange={(e) => setFormData({...formData, workArea: e.target.value})}
                  placeholder="e.g., Scaffolding area, Equipment zone"
                />
              </div>
            </div>
          </div>

          {/* Witness Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Witness Information</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="witness-count">Number of Witnesses</Label>
                <Input
                  id="witness-count"
                  type="number"
                  value={formData.witnessCount}
                  onChange={(e) => setFormData({...formData, witnessCount: e.target.value})}
                  placeholder="0"
                  min="0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="witnesses">Witness Names & Contact Information</Label>
              <Textarea
                id="witnesses"
                value={formData.witnesses}
                onChange={(e) => setFormData({...formData, witnesses: e.target.value})}
                placeholder="List witness names, positions, and contact details..."
                rows={2}
              />
            </div>
          </div>

          {/* Injury Information */}
          {formData.incidentType === 'injury' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <h4 className="font-medium text-red-800">Injury Information</h4>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="injured-person">Injured Person Name</Label>
                  <Input
                    id="injured-person"
                    value={formData.injuredPerson}
                    onChange={(e) => setFormData({...formData, injuredPerson: e.target.value})}
                    placeholder="Name of injured person"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="injured-person-company">Injured Person Company</Label>
                  <Input
                    id="injured-person-company"
                    value={formData.injuredPersonCompany}
                    onChange={(e) => setFormData({...formData, injuredPersonCompany: e.target.value})}
                    placeholder="Company/contractor name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="injury-type">Injury Type</Label>
                <Select value={formData.injuryType} onValueChange={(value) => setFormData({...formData, injuryType: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select injury type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cut-laceration">Cut/Laceration</SelectItem>
                    <SelectItem value="bruise-contusion">Bruise/Contusion</SelectItem>
                    <SelectItem value="sprain-strain">Sprain/Strain</SelectItem>
                    <SelectItem value="fracture">Fracture</SelectItem>
                    <SelectItem value="burn">Burn</SelectItem>
                    <SelectItem value="eye-injury">Eye Injury</SelectItem>
                    <SelectItem value="head-injury">Head Injury</SelectItem>
                    <SelectItem value="back-injury">Back Injury</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="medical-attention"
                    checked={formData.medicalAttention}
                    onChange={(e) => setFormData({...formData, medicalAttention: e.target.checked})}
                    className="rounded border-border"
                  />
                  <Label htmlFor="medical-attention">Medical Attention Required</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="hospital-transport"
                    checked={formData.hospitalTransport}
                    onChange={(e) => setFormData({...formData, hospitalTransport: e.target.checked})}
                    className="rounded border-border"
                  />
                  <Label htmlFor="hospital-transport">Hospital Transport Required</Label>
                </div>
              </div>

              {formData.medicalAttention && (
                <div className="space-y-2">
                  <Label htmlFor="hospital-name">Hospital Name</Label>
                  <Input
                    id="hospital-name"
                    value={formData.hospitalName}
                    onChange={(e) => setFormData({...formData, hospitalName: e.target.value})}
                    placeholder="Name of hospital/medical facility"
                  />
                </div>
              )}
            </div>
          )}

          {/* Immediate Response */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Immediate Response</h4>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="work-stopped"
                checked={formData.workStopped}
                onChange={(e) => setFormData({...formData, workStopped: e.target.checked})}
                className="rounded border-border"
              />
              <Label htmlFor="work-stopped">Work Stopped Immediately</Label>
            </div>

            <div className="space-y-2">
              <Label htmlFor="immediate-actions">Immediate Actions Taken</Label>
              <Textarea
                id="immediate-actions"
                value={formData.immediateActions}
                onChange={(e) => setFormData({...formData, immediateActions: e.target.value})}
                placeholder="Describe immediate actions taken to secure the area, assist injured persons, etc..."
                rows={3}
              />
            </div>
          </div>

          {/* Authority Reporting */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Authority Reporting</h4>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="reported-to-authorities"
                checked={formData.reportedToAuthorities}
                onChange={(e) => setFormData({...formData, reportedToAuthorities: e.target.checked})}
                className="rounded border-border"
              />
              <Label htmlFor="reported-to-authorities">Reported to Authorities (OSHA, etc.)</Label>
            </div>

            {formData.reportedToAuthorities && (
              <div className="space-y-2">
                <Label htmlFor="authority-report-number">Authority Report Number</Label>
                <Input
                  id="authority-report-number"
                  value={formData.authorityReportNumber}
                  onChange={(e) => setFormData({...formData, authorityReportNumber: e.target.value})}
                  placeholder="e.g., OSHA-2024-0315-001"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Upload Photos/Documents</Label>
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => document.getElementById('safety-file-upload-input')?.click()}
              >
                <input
                  id="safety-file-upload-input"
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  disabled={isUploading}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.heic"
                />
                <div className="flex flex-col items-center gap-2">
                  {isUploading ? (
                    <>
                      <Upload className="h-10 w-10 text-primary animate-pulse" />
                      <p className="text-sm font-medium text-primary">Uploading files...</p>
                      <p className="text-xs text-muted-foreground">Please wait</p>
                    </>
                  ) : (
                    <>
                      <Upload className="h-10 w-10 text-gray-400" />
                      <p className="text-sm font-medium">Click to upload or drag and drop</p>
                      <p className="text-xs text-muted-foreground">
                        Photos (PNG, JPG, HEIC), PDF, Word Documents (Max 10MB per file)
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Uploaded Files List */}
            {uploadedFiles.length > 0 && (
              <div className="space-y-2">
                <Label>Uploaded Files ({uploadedFiles.length})</Label>
                <div className="border rounded-md divide-y max-h-40 overflow-y-auto">
                  {uploadedFiles.map((file) => (
                    <div key={file.id} className="flex items-center justify-between p-3 hover:bg-gray-50">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <FileText className="h-4 w-4 text-primary flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{file.name}</p>
                          <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveFile(file.id)}
                        className="flex-shrink-0"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="incident-notes">Additional Notes</Label>
              <Textarea
                id="incident-notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Any additional information or follow-up actions..."
                rows={2}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={isSaving}>Save as Draft</Button>
          <Button onClick={handleSave} disabled={isSaving}>
            <AlertTriangle className="h-4 w-4 mr-2" />
            {isSaving ? 'Submitting...' : 'Submit Report'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Edit Safety Incident Modal Component
function EditSafetyIncidentModal({ incident, trigger, onIncidentUpdated, availableProjects }: { incident: any; trigger: React.ReactNode; onIncidentUpdated?: () => void; availableProjects: any[] }) {
  const [open, setOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Initialize form data with existing incident data
  const [formData, setFormData] = useState({
    title: incident.title || '',
    project: incident.project_id || '',
    incidentType: incident.incident_type || '',
    severity: incident.severity || '',
    description: incident.description || '',
    incidentDate: incident.incident_date ? incident.incident_date.split('T')[0] : '',
    incidentTime: incident.incident_date ? new Date(incident.incident_date).toTimeString().slice(0, 5) : '',
    location: incident.location || '',
    workArea: incident.work_area || '',
    witnessCount: incident.witness_count?.toString() || '',
    witnesses: incident.witness_names?.join('\n') || '',
    injuredPerson: incident.injured_person_name || '',
    injuredPersonCompany: incident.injured_person_company || '',
    injuryType: incident.injury_type || '',
    medicalAttention: incident.medical_attention_required || false,
    hospitalName: incident.hospital_name || '',
    hospitalTransport: incident.hospital_transport || false,
    workStopped: incident.work_stopped || false,
    immediateActions: incident.immediate_action_taken || '',
    reportedToAuthorities: incident.reported_to_authorities || false,
    authorityReportNumber: incident.authority_report_number || '',
    assignedTo: incident.assigned_to || '',
    status: incident.status || 'reported',
    notes: incident.notes || ''
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});

  // Update form data when incident changes
  useEffect(() => {
    if (open) {
      setFormData({
        title: incident.title || '',
        project: incident.project_id || '',
        incidentType: incident.incident_type || '',
        severity: incident.severity || '',
        description: incident.description || '',
        incidentDate: incident.incident_date ? incident.incident_date.split('T')[0] : '',
        incidentTime: incident.incident_date ? new Date(incident.incident_date).toTimeString().slice(0, 5) : '',
        location: incident.location || '',
        workArea: incident.work_area || '',
        witnessCount: incident.witness_count?.toString() || '',
        witnesses: incident.witness_names?.join('\n') || '',
        injuredPerson: incident.injured_person_name || '',
        injuredPersonCompany: incident.injured_person_company || '',
        injuryType: incident.injury_type || '',
        medicalAttention: incident.medical_attention_required || false,
        hospitalName: incident.hospital_name || '',
        hospitalTransport: incident.hospital_transport || false,
        workStopped: incident.work_stopped || false,
        immediateActions: incident.immediate_action_taken || '',
        reportedToAuthorities: incident.reported_to_authorities || false,
        authorityReportNumber: incident.authority_report_number || '',
        assignedTo: incident.assigned_to || '',
        status: incident.status || 'reported',
        notes: incident.notes || ''
      });
    }
  }, [open, incident]);

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.title) newErrors.title = 'Title is required';
    if (!formData.project) newErrors.project = 'Project is required';
    if (!formData.severity) newErrors.severity = 'Severity is required';
    if (!formData.incidentDate) newErrors.incidentDate = 'Incident date is required';
    if (!formData.location) newErrors.location = 'Location is required';
    if (!formData.description) newErrors.description = 'Description is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdate = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      const { safetyService } = await import('../../../src/services/safety.service');

      // Combine date and time for incident_date
      const incidentDateTime = formData.incidentTime
        ? `${formData.incidentDate}T${formData.incidentTime}:00`
        : `${formData.incidentDate}T12:00:00`;

      // Convert witnesses textarea to array
      const witnessNamesArray = formData.witnesses
        ? formData.witnesses.split('\n').map(w => w.trim()).filter(w => w.length > 0)
        : [];

      // Prepare update payload
      const payload = {
        title: formData.title || undefined,
        project_id: formData.project,
        incident_type: formData.incidentType || undefined,
        incident_date: incidentDateTime,
        location: formData.location,
        description: formData.description,
        severity: formData.severity as 'minor' | 'moderate' | 'major' | 'critical',
        medical_attention_required: formData.medicalAttention,
        work_area: formData.workArea || undefined,
        witness_count: formData.witnessCount ? parseInt(formData.witnessCount) : undefined,
        injured_person_name: formData.injuredPerson || undefined,
        injured_person_company: formData.injuredPersonCompany || undefined,
        injury_type: formData.injuryType || undefined,
        witness_names: witnessNamesArray.length > 0 ? witnessNamesArray : undefined,
        immediate_action_taken: formData.immediateActions || undefined,
        hospital_name: formData.hospitalName || undefined,
        hospital_transport: formData.hospitalTransport || undefined,
        work_stopped: formData.workStopped || undefined,
        reported_to_authorities: formData.reportedToAuthorities,
        authority_report_number: formData.authorityReportNumber || undefined,
        assigned_to: formData.assignedTo || undefined,
        status: formData.status || undefined,
        notes: formData.notes || undefined
      };

      const response = await safetyService.updateSafetyIncident(incident.incident_id, payload);

      if (response.success) {
        showSuccess({
          title: 'Incident Updated',
          message: `Incident ${incident.incident_number} has been updated successfully`
        });
        setOpen(false);

        // Call callback to reload incidents
        if (onIncidentUpdated) {
          onIncidentUpdated();
        }
      } else {
        showError({ title: 'Error', message: 'Failed to update safety incident' });
      }
    } catch (error: any) {
      console.error('Error updating safety incident:', error);
      showError({
        title: 'Error',
        message: error?.message || 'Failed to update safety incident'
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5" />
            Update Safety Incident - {incident.incident_number}
          </DialogTitle>
          <DialogDescription>
            Update the safety incident details below
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className={errors.title ? 'border-red-500' : ''}
              />
              {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
            </div>

            <div>
              <Label htmlFor="project">Project *</Label>
              <Select value={formData.project} onValueChange={(value) => setFormData({...formData, project: value})}>
                <SelectTrigger className={errors.project ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {availableProjects.map((project) => (
                    <SelectItem key={project.project_id} value={project.project_id}>
                      {project.project_name || project.name || 'Unnamed Project'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.project && <p className="text-red-500 text-sm mt-1">{errors.project}</p>}
            </div>

            <div>
              <Label htmlFor="severity">Severity *</Label>
              <Select value={formData.severity} onValueChange={(value) => setFormData({...formData, severity: value})}>
                <SelectTrigger className={errors.severity ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Select severity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="minor">Minor</SelectItem>
                  <SelectItem value="moderate">Moderate</SelectItem>
                  <SelectItem value="major">Major</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
              {errors.severity && <p className="text-red-500 text-sm mt-1">{errors.severity}</p>}
            </div>

            <div>
              <Label htmlFor="incidentType">Incident Type</Label>
              <Select value={formData.incidentType} onValueChange={(value) => setFormData({...formData, incidentType: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="injury">Injury</SelectItem>
                  <SelectItem value="near-miss">Near Miss</SelectItem>
                  <SelectItem value="property-damage">Property Damage</SelectItem>
                  <SelectItem value="environmental">Environmental</SelectItem>
                  <SelectItem value="equipment-failure">Equipment Failure</SelectItem>
                  <SelectItem value="first-aid">First Aid</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="incidentDate">Date *</Label>
                <Input
                  id="incidentDate"
                  type="date"
                  value={formData.incidentDate}
                  onChange={(e) => setFormData({...formData, incidentDate: e.target.value})}
                  className={errors.incidentDate ? 'border-red-500' : ''}
                />
                {errors.incidentDate && <p className="text-red-500 text-sm mt-1">{errors.incidentDate}</p>}
              </div>
              <div>
                <Label htmlFor="incidentTime">Time</Label>
                <Input
                  id="incidentTime"
                  type="time"
                  value={formData.incidentTime}
                  onChange={(e) => setFormData({...formData, incidentTime: e.target.value})}
                />
              </div>
            </div>
          </div>

          {/* Location & Description */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="location">Location *</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                className={errors.location ? 'border-red-500' : ''}
              />
              {errors.location && <p className="text-red-500 text-sm mt-1">{errors.location}</p>}
            </div>

            <div>
              <Label htmlFor="workArea">Work Area</Label>
              <Input
                id="workArea"
                value={formData.workArea}
                onChange={(e) => setFormData({...formData, workArea: e.target.value})}
              />
            </div>

            <div>
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className={errors.description ? 'border-red-500' : ''}
              />
              {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
            </div>

            <div>
              <Label htmlFor="immediateActions">Immediate Actions Taken</Label>
              <Textarea
                id="immediateActions"
                rows={3}
                value={formData.immediateActions}
                onChange={(e) => setFormData({...formData, immediateActions: e.target.value})}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={isSaving}>Cancel</Button>
          <Button onClick={handleUpdate} disabled={isSaving}>
            <CheckCircle className="h-4 w-4 mr-2" />
            {isSaving ? 'Updating...' : 'Update Incident'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// View Safety Incident Details Modal Component
function ViewIncidentDetailsModal({ incident, trigger, projects, users }: { incident: any; trigger: React.ReactNode; projects: any[]; users: Map<string, string> }) {
  const [isOpen, setIsOpen] = useState(false);
  const getProjectName = (projectId: string) => {
    const project = projects.find(p => p.project_id === projectId);
    return project?.project_name || project?.name || 'Unknown Project';
  };

  const getUserName = (userId: string) => {
    return users.get(userId) || userId || 'Unknown User';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatTime = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild onClick={() => setIsOpen(true)}>
        {trigger}
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Safety Incident Details - {incident.incident_number}
          </DialogTitle>
          <DialogDescription>
            Complete details of the safety incident report
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Information */}
          <div className="border rounded-lg p-4">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Basic Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground">Incident Number</Label>
                <p className="font-medium">{incident.incident_number}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Title</Label>
                <p className="font-medium">{incident.title}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Project</Label>
                <p className="font-medium">{getProjectName(incident.project_id)}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Status</Label>
                <p className="font-medium capitalize">{incident.status}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Severity</Label>
                <p className="font-medium capitalize">{incident.severity}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Incident Type</Label>
                <p className="font-medium">{incident.incident_type}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Date & Time</Label>
                <p className="font-medium">{formatDate(incident.incident_date)} at {formatTime(incident.incident_date)}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Reported By</Label>
                <p className="font-medium">{getUserName(incident.reported_by)}</p>
              </div>
            </div>
          </div>

          {/* Location & Description */}
          <div className="border rounded-lg p-4">
            <h3 className="font-semibold mb-4">Location & Description</h3>
            <div className="space-y-4">
              <div>
                <Label className="text-muted-foreground">Location</Label>
                <p className="font-medium">{incident.location}</p>
              </div>
              {incident.work_area && (
                <div>
                  <Label className="text-muted-foreground">Work Area</Label>
                  <p className="font-medium">{incident.work_area}</p>
                </div>
              )}
              <div>
                <Label className="text-muted-foreground">Description</Label>
                <p className="font-medium whitespace-pre-wrap">{incident.description}</p>
              </div>
            </div>
          </div>

          {/* Injury Details */}
          {incident.injured_person_name && (
            <div className="border rounded-lg p-4 bg-red-50">
              <h3 className="font-semibold mb-4 text-red-800">Injury Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Injured Person</Label>
                  <p className="font-medium">{incident.injured_person_name}</p>
                </div>
                {incident.injured_person_company && (
                  <div>
                    <Label className="text-muted-foreground">Company</Label>
                    <p className="font-medium">{incident.injured_person_company}</p>
                  </div>
                )}
                {incident.injury_type && (
                  <div>
                    <Label className="text-muted-foreground">Injury Type</Label>
                    <p className="font-medium capitalize">{incident.injury_type.replace('-', ' ')}</p>
                  </div>
                )}
                <div>
                  <Label className="text-muted-foreground">Medical Attention Required</Label>
                  <p className="font-medium">{incident.medical_attention_required ? 'Yes' : 'No'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Response & Actions */}
          <div className="border rounded-lg p-4">
            <h3 className="font-semibold mb-4">Response & Actions</h3>
            <div className="space-y-4">
              {incident.immediate_action_taken && (
                <div>
                  <Label className="text-muted-foreground">Immediate Actions Taken</Label>
                  <p className="font-medium whitespace-pre-wrap">{incident.immediate_action_taken}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Work Stopped</Label>
                  <p className="font-medium">{incident.work_stopped ? 'Yes' : 'No'}</p>
                </div>
                {incident.witness_count > 0 && (
                  <div>
                    <Label className="text-muted-foreground">Witnesses</Label>
                    <p className="font-medium">{incident.witness_count} witness(es)</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Investigation Details */}
          {incident.root_cause && (
            <div className="border rounded-lg p-4 bg-orange-50">
              <h3 className="font-semibold mb-4 text-orange-800">Investigation Results</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-muted-foreground">Root Cause</Label>
                  <p className="font-medium whitespace-pre-wrap">{incident.root_cause}</p>
                </div>
                {incident.corrective_actions && (
                  <div>
                    <Label className="text-muted-foreground">Corrective Actions</Label>
                    <p className="font-medium whitespace-pre-wrap">{incident.corrective_actions}</p>
                  </div>
                )}
                {incident.preventive_actions && (
                  <div>
                    <Label className="text-muted-foreground">Preventive Actions</Label>
                    <p className="font-medium whitespace-pre-wrap">{incident.preventive_actions}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Photos & Attachments */}
          {incident.photos && incident.photos.length > 0 && (
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Paperclip className="h-4 w-4" />
                Photos & Attachments ({incident.photos.length})
              </h3>
              <div className="space-y-2">
                {incident.photos.map((photo: any, index: number) => {
                  const storageId = typeof photo === 'string' ? photo : (photo.file_id || photo.storage_id || photo.file_path);
                  return (
                    <div key={index} className="flex items-center justify-between border rounded p-2 hover:bg-muted/30">
                      <div className="flex items-center gap-2 text-sm">
                        <FileText className="h-4 w-4 text-primary" />
                        <span>Attachment {index + 1}</span>
                      </div>
                      {storageId && (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2"
                            onClick={async () => {
                              try {
                                const res = await fileStorageService.previewFile(storageId);
                                if (res.success && res.data) {
                                  const url = URL.createObjectURL(res.data);
                                  window.open(url, '_blank');
                                  setTimeout(() => URL.revokeObjectURL(url), 1000);
                                }
                              } catch (error: any) {
                                showError({ title: 'Preview failed', message: error.message || 'Failed to preview file' });
                              }
                            }}
                          >
                            <Eye className="h-3 w-3 mr-1" />Preview
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2"
                            onClick={async () => {
                              try {
                                const res = await fileStorageService.downloadFile(storageId);
                                if (res.success && res.data) {
                                  const url = URL.createObjectURL(res.data);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `attachment-${index + 1}`;
                                  document.body.appendChild(a);
                                  a.click();
                                  document.body.removeChild(a);
                                  setTimeout(() => URL.revokeObjectURL(url), 1000);
                                }
                              } catch (error: any) {
                                showError({ title: 'Download failed', message: error.message || 'Failed to download file' });
                              }
                            }}
                          >
                            <Download className="h-3 w-3 mr-1" />Download
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Additional Notes */}
          {incident.notes && (
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold mb-4">Additional Notes</h3>
              <p className="font-medium whitespace-pre-wrap">{incident.notes}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Safety Incident Investigation Modal Component
function IncidentInvestigationModal({ incident, trigger, onInvestigationComplete }: { incident: any; trigger: React.ReactNode; onInvestigationComplete?: () => void }) {
  const [investigationData, setInvestigationData] = useState({
    // Investigation form fields (all retained)
    status: 'under-investigation',
    findings: '',
    rootCause: '',
    contributingFactors: '',
    correctiveActions: '',
    preventiveActions: '',
    responsiblePerson: '',
    targetDate: '',
    investigatedBy: 'Current User',
    investigationDate: new Date().toISOString().split('T')[0]
  });

  const handleSubmitInvestigation = async () => {
    try {
      const { safetyService } = await import('../../../src/services/safety.service');

      // Prepare investigation payload with ALL form fields
      const investigationPayload = {
        status: investigationData.status || undefined,
        findings: investigationData.findings || undefined,
        root_cause: investigationData.rootCause,
        contributing_factors: investigationData.contributingFactors || undefined,
        corrective_actions: investigationData.correctiveActions,
        preventive_actions: investigationData.preventiveActions || undefined,
        preventive_measures: investigationData.preventiveActions || '', // Alias for compatibility
        responsible_person: investigationData.responsiblePerson || undefined,
        target_date: investigationData.targetDate || undefined,
        investigated_by: investigationData.investigatedBy || undefined,
        investigation_date: investigationData.investigationDate || undefined
      };

      const response = await safetyService.completeInvestigation(
        incident.incident_id,
        investigationPayload
      );

      if (response.success) {
        showSuccess({
          title: 'Investigation Completed',
          message: 'Investigation has been completed successfully'
        });

        // Call callback to reload incidents
        if (onInvestigationComplete) {
          onInvestigationComplete();
        }
      } else {
        showError({ title: 'Error', message: 'Failed to complete investigation' });
      }
    } catch (error: any) {
      console.error('Error completing investigation:', error);
      showError({
        title: 'Error',
        message: error?.message || 'Failed to complete investigation'
      });
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Investigation Report
          </DialogTitle>
          <DialogDescription>
            Complete investigation for: {incident.title}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium mb-2">Incident Summary</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">Type:</span>
                <p className="text-muted-foreground">{incident.incidentType}</p>
              </div>
              <div>
                <span className="font-medium">Severity:</span>
                <p className="text-muted-foreground">{incident.severity}</p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="investigation-status">Investigation Status</Label>
            <Select value={investigationData.status} onValueChange={(value) => setInvestigationData({...investigationData, status: value})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="under-investigation">Under Investigation</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="pending-review">Pending Review</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="investigation-findings">Investigation Findings</Label>
            <Textarea
              id="investigation-findings"
              value={investigationData.findings}
              onChange={(e) => setInvestigationData({...investigationData, findings: e.target.value})}
              placeholder="Document detailed investigation findings..."
              rows={4}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="root-cause-analysis">Root Cause Analysis</Label>
            <Textarea
              id="root-cause-analysis"
              value={investigationData.rootCause}
              onChange={(e) => setInvestigationData({...investigationData, rootCause: e.target.value})}
              placeholder="Identify the root cause of the incident..."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contributing-factors">Contributing Factors</Label>
            <Textarea
              id="contributing-factors"
              value={investigationData.contributingFactors}
              onChange={(e) => setInvestigationData({...investigationData, contributingFactors: e.target.value})}
              placeholder="List any contributing factors or conditions..."
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="corrective-actions">Corrective Actions</Label>
            <Textarea
              id="corrective-actions"
              value={investigationData.correctiveActions}
              onChange={(e) => setInvestigationData({...investigationData, correctiveActions: e.target.value})}
              placeholder="Immediate corrective actions to address this incident..."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="preventive-actions">Preventive Actions</Label>
            <Textarea
              id="preventive-actions"
              value={investigationData.preventiveActions}
              onChange={(e) => setInvestigationData({...investigationData, preventiveActions: e.target.value})}
              placeholder="Long-term preventive measures to prevent recurrence..."
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="responsible-person">Responsible Person</Label>
              <Input
                id="responsible-person"
                value={investigationData.responsiblePerson}
                onChange={(e) => setInvestigationData({...investigationData, responsiblePerson: e.target.value})}
                placeholder="Person responsible for action implementation"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="target-date">Target Completion Date</Label>
              <Input
                id="target-date"
                type="date"
                value={investigationData.targetDate}
                onChange={(e) => setInvestigationData({...investigationData, targetDate: e.target.value})}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline">Save as Draft</Button>
          <Button onClick={handleSubmitInvestigation}>
            <CheckCircle className="h-4 w-4 mr-2" />
            Complete Investigation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Safety Incident Actions Menu Component
function SafetyIncidentActionsMenu({ incident, onRefresh, projects, users }: { incident: any; onRefresh: () => void; projects: any[]; users: Map<string, string> }) {
  const handleCloseIncident = async () => {
    try {
      const { safetyService } = await import('../../../src/services/safety.service');
      const response = await safetyService.closeIncident(incident.incident_id);

      if (response.success) {
        showSuccess({
          title: 'Incident Closed',
          message: 'The safety incident has been closed successfully'
        });
        onRefresh();
      } else {
        showError({ title: 'Error', message: 'Failed to close incident' });
      }
    } catch (error: any) {
      console.error('Error closing incident:', error);
      showError({
        title: 'Error',
        message: error?.message || 'Failed to close incident'
      });
    }
  };

  const handleExportReport = async () => {
    try {
      const { pdfUtils } = await import('../../../src/utils/pdf.utils');

      const getProjectName = (projectId: string) => {
        const project = projects.find(p => p.project_id === projectId);
        return project?.project_name || project?.name || 'Unknown Project';
      };

      const getUserName = (userId: string) => {
        return users.get(userId) || userId || 'Unknown User';
      };

      const formatDate = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      };

      const formatTime = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      };

      // Create PDF with branding
      const doc = pdfUtils.createPDF({
        title: 'Safety Incident Report',
        subtitle: incident.title,
        documentNumber: incident.incident_number,
        date: formatDate(new Date().toISOString()),
        footer: `Generated by OptiBuild - Confidential`
      });

      let y = 70;

      // Basic Information Section
      y = pdfUtils.addSectionHeader(doc, 'Basic Information', y);
      y = pdfUtils.addKeyValueGrid(doc, [
        { label: 'Incident Number', value: incident.incident_number },
        { label: 'Title', value: incident.title || 'N/A' },
        { label: 'Project', value: getProjectName(incident.project_id) },
        { label: 'Status', value: incident.status || 'N/A' },
        { label: 'Severity', value: incident.severity || 'N/A' },
        { label: 'Incident Type', value: incident.incident_type || 'N/A' },
        { label: 'Date & Time', value: `${formatDate(incident.incident_date)} at ${formatTime(incident.incident_date)}` },
        { label: 'Reported By', value: getUserName(incident.reported_by) }
      ], y);

      y += 5;

      // Location & Description
      y = pdfUtils.addSectionHeader(doc, 'Location & Description', y);
      y = pdfUtils.addTextBlock(doc, 'Location:', incident.location || 'N/A', y);
      if (incident.work_area) {
        y = pdfUtils.addTextBlock(doc, 'Work Area:', incident.work_area, y);
      }
      y = pdfUtils.addTextBlock(doc, 'Description:', incident.description || 'N/A', y);

      y += 5;

      // Injury Details (if applicable)
      if (incident.injured_person_name) {
        y = pdfUtils.addSectionHeader(doc, 'Injury Details', y);
        y = pdfUtils.addKeyValueGrid(doc, [
          { label: 'Injured Person', value: incident.injured_person_name },
          { label: 'Company', value: incident.injured_person_company || 'N/A' },
          { label: 'Injury Type', value: incident.injury_type || 'N/A' },
          { label: 'Medical Attention', value: incident.medical_attention_required ? 'Yes' : 'No' }
        ], y);
        y += 5;
      }

      // Response & Actions
      y = pdfUtils.addSectionHeader(doc, 'Response & Actions', y);
      if (incident.immediate_action_taken) {
        y = pdfUtils.addTextBlock(doc, 'Immediate Actions Taken:', incident.immediate_action_taken, y);
      }
      y = pdfUtils.addKeyValueGrid(doc, [
        { label: 'Work Stopped', value: incident.work_stopped ? 'Yes' : 'No' },
        { label: 'Witnesses', value: incident.witness_count || 0 }
      ], y);

      y += 5;

      // Investigation Results (if available)
      if (incident.root_cause) {
        y = pdfUtils.addSectionHeader(doc, 'Investigation Results', y);
        y = pdfUtils.addTextBlock(doc, 'Root Cause:', incident.root_cause, y);
        if (incident.corrective_actions) {
          y = pdfUtils.addTextBlock(doc, 'Corrective Actions:', incident.corrective_actions, y);
        }
        if (incident.preventive_actions) {
          y = pdfUtils.addTextBlock(doc, 'Preventive Actions:', incident.preventive_actions, y);
        }
        y += 5;
      }

      // Additional Notes
      if (incident.notes) {
        y = pdfUtils.addSectionHeader(doc, 'Additional Notes', y);
        y = pdfUtils.addTextBlock(doc, '', incident.notes, y);
      }

      // Export
      await pdfUtils.exportToPDF(
        doc,
        `Safety_Incident_${incident.incident_number}_${new Date().toISOString().split('T')[0]}.pdf`,
        {
          title: 'Safety Incident Report',
          subtitle: incident.title,
          documentNumber: incident.incident_number,
          date: formatDate(new Date().toISOString())
        }
      );

      showSuccess({
        title: 'Report Exported',
        message: 'Safety incident report has been exported to PDF'
      });
    } catch (error: any) {
      console.error('Error exporting report:', error);
      showError({
        title: 'Export Failed',
        message: error?.message || 'Failed to export report to PDF'
      });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm">
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <ViewIncidentDetailsModal
          incident={incident}
          projects={projects}
          users={users}
          trigger={
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
              <Eye className="h-4 w-4 mr-2" />
              View Details
            </DropdownMenuItem>
          }
        />
        <IncidentInvestigationModal
          incident={incident}
          onInvestigationComplete={onRefresh}
          trigger={
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
              <FileText className="h-4 w-4 mr-2" />
              Investigation
            </DropdownMenuItem>
          }
        />
        <EditSafetyIncidentModal
          incident={incident}
          availableProjects={projects}
          onIncidentUpdated={onRefresh}
          trigger={
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
              <Edit className="h-4 w-4 mr-2" />
              Update Report
            </DropdownMenuItem>
          }
        />
        <DropdownMenuItem onClick={handleExportReport}>
          <Download className="h-4 w-4 mr-2" />
          Export Report
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleCloseIncident}>
          <Archive className="h-4 w-4 mr-2" />
          Close Incident
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function SafetyIncidents() {
  const [searchQuery, setSearchQuery] = useState("");
  const [projectFilter, setProjectFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [incidents, setIncidents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<any[]>([]);
  const [users, setUsers] = useState<Map<string, string>>(new Map());

  // Fetch incidents from backend
  useEffect(() => {
    loadIncidents();
    loadProjects();
    loadUsers();
  }, []);

  const loadIncidents = async () => {
    setLoading(true);
    try {
      const { safetyService } = await import('../../../src/services/safety.service');
      const response = await safetyService.getSafetyIncidents();
      if (response.success && response.data) {
        const incidentsData = Array.isArray(response.data) ? response.data : response.data.data || [];
        setIncidents(incidentsData);
      }
    } catch (error) {
      console.error('Error loading safety incidents:', error);
      showError({ title: 'Error', message: 'Failed to load safety incidents' });
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await projectService.getProjects();
      if (response.success && response.data) {
        const projectData = Array.isArray(response.data) ? response.data : response.data.data || [];
        setProjects(projectData);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const { userService } = await import('../../../src/services/user.service');
      const response = await userService.getUsers();
      if (response.success && response.data) {
        const usersData = Array.isArray(response.data) ? response.data : (response.data as any).data || [];
        const userMap = new Map<string, string>();
        usersData.forEach((user: any) => {
          const fullName = user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || user.email;
          userMap.set(user.user_id || user.id, fullName);
        });
        setUsers(userMap);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  // Helper function to get project name by ID
  const getProjectName = (projectId: string) => {
    const project = projects.find(p => p.project_id === projectId);
    return project?.project_name || project?.name || 'Unknown Project';
  };

  // Helper function to get user name by ID
  const getUserName = (userId: string) => {
    return users.get(userId) || userId || 'Unknown User';
  };

  const filteredIncidents = incidents.filter(incident => {
    const matchesSearch = (incident.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (incident.description || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (incident.incident_number || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesProject = projectFilter === "all" || incident.project_id === projectFilter;
    const matchesStatus = statusFilter === "all" || incident.status === statusFilter;
    const matchesType = typeFilter === "all" || incident.incident_type === typeFilter;
    const matchesSeverity = severityFilter === "all" || incident.severity === severityFilter;
    return matchesSearch && matchesProject && matchesStatus && matchesType && matchesSeverity;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "reported": return <Badge variant="secondary">Reported</Badge>;
      case "under-investigation": return <Badge variant="default" className="bg-orange-600">Under Investigation</Badge>;
      case "investigation-complete": return <Badge variant="default" className="bg-blue-600">Investigation Complete</Badge>;
      case "closed": return <Badge variant="default" className="bg-green-600">Closed</Badge>;
      case "pending-review": return <Badge variant="outline">Pending Review</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case "fatal": return <Badge variant="destructive" className="bg-black">Fatal</Badge>;
      case "major": return <Badge variant="destructive">Major</Badge>;
      case "serious": return <Badge className="bg-red-600">Serious</Badge>;
      case "moderate": return <Badge className="bg-orange-600">Moderate</Badge>;
      case "minor": return <Badge variant="secondary">Minor</Badge>;
      default: return <Badge variant="outline">{severity}</Badge>;
    }
  };

  const getIncidentTypeIcon = (type: string) => {
    switch (type) {
      case "injury": return "🏥";
      case "near-miss": return "⚠️";
      case "property-damage": return "💥";
      case "environmental": return "🌍";
      case "fire": return "🔥";
      case "equipment-failure": return "⚙️";
      case "unsafe-condition": return "🚧";
      case "unsafe-act": return "⚠️";
      case "first-aid": return "🩹";
      case "security": return "🔒";
      default: return "📋";
    }
  };

  // Helper function to format incident type to human-readable
  const getIncidentTypeLabel = (type: string) => {
    const labels: {[key: string]: string} = {
      "injury": "Personal Injury",
      "near-miss": "Near Miss",
      "property-damage": "Property Damage",
      "environmental": "Environmental Incident",
      "fire": "Fire/Explosion",
      "equipment-failure": "Equipment Failure",
      "unsafe-condition": "Unsafe Condition",
      "unsafe-act": "Unsafe Act",
      "first-aid": "First Aid Case",
      "security": "Security Incident"
    };
    return labels[type] || type;
  };

  // Helper function to format injury type to human-readable
  const getInjuryTypeLabel = (type: string) => {
    const labels: {[key: string]: string} = {
      "cut-laceration": "Cut/Laceration",
      "bruise-contusion": "Bruise/Contusion",
      "sprain-strain": "Sprain/Strain",
      "fracture": "Fracture",
      "burn": "Burn",
      "eye-injury": "Eye Injury",
      "head-injury": "Head Injury",
      "back-injury": "Back Injury",
      "other": "Other"
    };
    return labels[type] || type;
  };

  // Helper function to format date
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Helper function to format time
  const formatTime = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Safety Incidents</h2>
          <p className="text-muted-foreground">Track and manage safety incidents and near misses</p>
        </div>
        <NewSafetyIncidentModal onIncidentCreated={loadIncidents} />
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Shield className="h-4 w-4 text-blue-600" />
              Total Incidents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{incidents.length}</div>
            <p className="text-xs text-blue-600 mt-1">All projects</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Search className="h-4 w-4 text-orange-600" />
              Under Investigation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-700">{incidents.filter(i => i.status === 'under-investigation').length}</div>
            <p className="text-xs text-orange-600 mt-1">Active investigations</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Days Without Incident
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">0</div>
            <p className="text-xs text-green-600 mt-1">Current streak</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-purple-600" />
              Near Misses
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-700">{incidents.filter(i => i.incident_type === 'near-miss').length}</div>
            <p className="text-xs text-purple-600 mt-1">Potential incidents</p>
          </CardContent>
        </Card>
      </div>

      {/* Safety Incident Management */}
      <Card>
        <CardHeader>
          <CardTitle>Safety Incident Register</CardTitle>
          <CardDescription>Manage safety incidents, investigations, and follow-up actions</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex items-center gap-4 mb-6 flex-wrap">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search incidents..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects.map((project) => (
                  <SelectItem key={project.project_id} value={project.project_id}>
                    {project.project_name || project.name || 'Unnamed Project'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="reported">Reported</SelectItem>
                <SelectItem value="under-investigation">Under Investigation</SelectItem>
                <SelectItem value="investigation-complete">Investigation Complete</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="injury">Injury</SelectItem>
                <SelectItem value="near-miss">Near Miss</SelectItem>
                <SelectItem value="property-damage">Property Damage</SelectItem>
                <SelectItem value="environmental">Environmental</SelectItem>
                <SelectItem value="equipment-failure">Equipment Failure</SelectItem>
              </SelectContent>
            </Select>
            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severity</SelectItem>
                <SelectItem value="fatal">Fatal</SelectItem>
                <SelectItem value="major">Major</SelectItem>
                <SelectItem value="serious">Serious</SelectItem>
                <SelectItem value="moderate">Moderate</SelectItem>
                <SelectItem value="minor">Minor</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Incident List */}
          <div className="space-y-4">
            {loading ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Loading safety incidents...</p>
              </div>
            ) : filteredIncidents.map((incident) => (
              <div key={incident.incident_id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xl">{getIncidentTypeIcon(incident.incident_type)}</span>
                      <span className="font-mono text-sm font-medium">{incident.incident_number}</span>
                      <h4 className="font-medium">{incident.title}</h4>
                      {getStatusBadge(incident.status)}
                      {getSeverityBadge(incident.severity)}
                      {incident.medical_attention_required && <Badge variant="outline" className="text-red-700 border-red-300">Medical Attention</Badge>}
                      {incident.work_stopped && <Badge variant="outline" className="text-orange-700 border-orange-300">Work Stopped</Badge>}
                    </div>

                    <p className="text-sm text-muted-foreground mb-3">{incident.description}</p>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                      <div>
                        <span className="font-medium">Project:</span>
                        <p className="text-muted-foreground">{getProjectName(incident.project_id)}</p>
                      </div>
                      <div>
                        <span className="font-medium">Location:</span>
                        <p className="text-muted-foreground">{incident.location}</p>
                      </div>
                      <div>
                        <span className="font-medium">Date & Time:</span>
                        <p className="text-muted-foreground">{formatDate(incident.incident_date)} {formatTime(incident.incident_date)}</p>
                      </div>
                      <div>
                        <span className="font-medium">Reported By:</span>
                        <p className="text-muted-foreground">{getUserName(incident.reported_by)}</p>
                      </div>
                    </div>

                    {incident.injured_person_name && (
                      <div className="grid grid-cols-2 gap-4 text-sm mb-2">
                        <div>
                          <span className="font-medium">Injured Person:</span>
                          <p className="text-muted-foreground">{incident.injured_person_name}</p>
                        </div>
                        <div>
                          <span className="font-medium">Injury Type:</span>
                          <p className="text-muted-foreground">{getInjuryTypeLabel(incident.injury_type)}</p>
                        </div>
                      </div>
                    )}

                    {incident.immediate_action_taken && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <span className="font-medium text-blue-800 text-sm">Immediate Actions: </span>
                        <span className="text-sm text-blue-700">{incident.immediate_action_taken}</span>
                      </div>
                    )}

                    {incident.root_cause && (
                      <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                        <span className="font-medium text-orange-800 text-sm">Root Cause: </span>
                        <span className="text-sm text-orange-700">{incident.root_cause}</span>
                      </div>
                    )}

                    {incident.witness_count > 0 && (
                      <div className="mt-3 flex items-center gap-4 text-sm">
                        <span>👥 {incident.witness_count} witnesses</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <ViewIncidentDetailsModal
                      incident={incident}
                      projects={projects}
                      users={users}
                      trigger={
                        <Button variant="outline" size="sm">
                          <Eye className="h-4 w-4" />
                        </Button>
                      }
                    />
                    {(incident.status === 'reported' || incident.status === 'under-investigation') && (
                      <IncidentInvestigationModal
                        incident={incident}
                        onInvestigationComplete={loadIncidents}
                        trigger={
                          <Button variant="default" size="sm">
                            <FileText className="h-4 w-4 mr-1" />
                            Investigate
                          </Button>
                        }
                      />
                    )}
                    <SafetyIncidentActionsMenu incident={incident} onRefresh={loadIncidents} projects={projects} users={users} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredIncidents.length === 0 && (
            <div className="text-center py-12">
              <AlertTriangle className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No incidents found</h3>
              <p className="text-muted-foreground">
                {searchQuery || projectFilter !== "all" || statusFilter !== "all" || typeFilter !== "all" || severityFilter !== "all"
                  ? "Try adjusting your search criteria or filters"
                  : "No safety incidents recorded - keep up the good work!"
                }
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
